import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import authSync from './services/authSync.js'

// KaTeX CSS for math rendering - this is small and needed early
import 'katex/dist/katex.min.css'

// Expose DOMPurify globally (used by components for sanitization)
import DOMPurify from 'dompurify'
if (typeof window !== 'undefined') {
  window.DOMPurify = DOMPurify;
}

const app = createApp(App)

// ===== AUTH SYNC - AUTO LOGOUT/LOGIN ACROSS TABS =====
// Initialize auth sync listener that will automatically handle login/logout
// events from other tabs in the same browser
if (typeof window !== 'undefined') {
  authSync.onAuthEvent((event) => {
    console.log('[main.js] Auth event received:', event.type);
    
    if (event.type === 'LOGIN') {
      console.log('[main.js] User logged in from another tab');
      // Update localStorage with new user data
      try {
        localStorage.setItem('user', JSON.stringify(event.userData));
      } catch (e) {
        console.error('[main.js] Failed to update localStorage:', e);
      }
      // Dispatch custom event for any interested components
      try {
        window.dispatchEvent(new CustomEvent('auth:login-sync', { detail: event.userData }));
      } catch (e) {
        console.error('[main.js] Failed to dispatch login event:', e);
      }
    } else if (event.type === 'LOGOUT') {
      console.log('[main.js] User logged out from another tab');
      // Clear all auth data
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      localStorage.removeItem('authToken');
      // Clear cookies
      const cookies = ['user', 'authToken', 'token'];
      cookies.forEach(name => {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; secure; samesite=strict`;
      });
      // Dispatch custom event for any interested components
      try {
        window.dispatchEvent(new CustomEvent('auth:logout-sync'));
      } catch (e) {
        console.error('[main.js] Failed to dispatch logout event:', e);
      }
      // Redirect to home and reload
      setTimeout(() => {
        if (window.location.pathname !== '/') {
          window.location.href = '/';
        } else {
          window.location.reload();
        }
      }, 100);
    }
  });
//  console.log('[main.js] Auth sync initialized');
}

// ===== LAZY LOAD TOAST PLUGIN - ON DEMAND ONLY =====
let toastPluginLoaded = false;
let pendingToasts = [];
let toastLoadingPromise = null;

// Async function to load toast plugin only when actually needed
const loadToastPlugin = async () => {
  if (toastPluginLoaded) return;
  if (toastLoadingPromise) return toastLoadingPromise;

  toastLoadingPromise = (async () => {
    try {
      const [ToastPlugin, , { toast: localToast }] = await Promise.all([
        import('vue-toast-notification'),
        import('vue-toast-notification/dist/theme-sugar.css'),
        import('@/composables/useToast')
      ]);

      // Install the actual toast plugin
      app.use(ToastPlugin.default, {
        position: 'top-right',
        duration: 3000,
        dismissible: true,
        queue: true,
        pauseOnHover: true,
        maxToasts: 3,
        newestOnTop: true
      });

      // Replace proxy with real toast
      app.config.globalProperties.$toast = localToast;
      app.config.globalProperties.$showToast = {
        success(message, options = {}) {
          localToast.success(message, { duration: options.duration || 3000 });
        },
        error(message, options = {}) {
          localToast.error(message, { duration: options.duration || 5000 });
        },
        warning(message, options = {}) {
          localToast.error(message, { duration: options.duration || 4000 });
        },
        info(message, options = {}) {
          localToast.success(message, { duration: options.duration || 3000 });
        }
      };

      toastPluginLoaded = true;

      // Process queued toasts
      pendingToasts.forEach(({ type, message, options }) => {
        if (localToast[type]) {
          localToast[type](message, options);
        }
      });
      pendingToasts = [];
    } catch (error) {
      console.error('Failed to load toast plugin:', error);
      toastLoadingPromise = null;
      throw error;
    }
  })();

  return toastLoadingPromise;
};

// Create a proxy toast system that loads plugin on first use
const createLazyToast = () => {
  const queueToast = async (type, message, options = {}) => {
    if (!toastPluginLoaded) {
      // Queue the toast
      pendingToasts.push({ type, message, options });
      // Load plugin on demand
      await loadToastPlugin();
    } else {
      // Plugin already loaded, use it directly
      if (app.config.globalProperties.$toast?.[type]) {
        app.config.globalProperties.$toast[type](message, options);
      }
    }
  };

  return {
    success: (message, options) => queueToast('success', message, options),
    error: (message, options) => queueToast('error', message, options),
    warning: (message, options) => queueToast('warning', message, options),
    info: (message, options) => queueToast('info', message, options)
  };
};

// Initialize lazy toast
const lazyToast = createLazyToast();
app.config.globalProperties.$toast = lazyToast;
app.config.globalProperties.$showToast = {
  success(message, options = {}) {
    lazyToast.success(message, { duration: options.duration || 3000 });
  },
  error(message, options = {}) {
    lazyToast.error(message, { duration: options.duration || 5000 });
  },
  warning(message, options = {}) {
    lazyToast.error(message, { duration: options.duration || 4000 });
  },
  info(message, options = {}) {
    lazyToast.success(message, { duration: options.duration || 3000 });
  }
};

// Unified toast function for consistent usage across the website
app.config.globalProperties.$showToastMessage = function(type, message, options = {}) {
  const defaultOptions = {
    success: { duration: 3000 },
    error: { duration: 5000 },
    warning: { duration: 4000 },
    info: { duration: 3000 }
  };

  const toastOptions = { ...defaultOptions[type], ...options };

  if (this.$showToast && typeof this.$showToast[type] === 'function') {
    this.$showToast[type](message, toastOptions);
  } else if (this.$toast && typeof this.$toast[type] === 'function') {
    this.$toast[type](message, toastOptions);
  } else {
    // Fallback to console if toast systems aren't available
    console.log(`[${type.toUpperCase()}] ${message}`);
  }
};

// ===== LAZY LOAD RECAPTCHA - ON DEMAND ONLY =====
let recaptchaLoaded = false;
let recaptchaLoadingPromise = null;
const recaptchaSiteKey = '6LeY8C4rAAAAAIPfhXXxzXGWXI-pCXvvkSRee5Pa';

// Global function to load reCAPTCHA only when needed
window.loadRecaptcha = async () => {
  if (recaptchaLoaded) return;
  if (recaptchaLoadingPromise) return recaptchaLoadingPromise;

  recaptchaLoadingPromise = (async () => {
    try {
      const { VueReCaptcha } = await import('vue-recaptcha-v3');
      
      app.use(VueReCaptcha, {
        siteKey: recaptchaSiteKey,
        loaderOptions: {
          autoHideBadge: true,
          useRecaptchaNet: true
        }
      });

      recaptchaLoaded = true;
      console.log('reCAPTCHA loaded on demand');
    } catch (error) {
      console.error('Failed to load reCAPTCHA:', error);
      recaptchaLoadingPromise = null;
      throw error;
    }
  })();

  return recaptchaLoadingPromise;
};

// Mount the app
app.use(router);
app.mount('#app');

// Note: Toast and reCAPTCHA will now load ONLY when:
// - Toast: When first toast message is triggered
// - reCAPTCHA: When window.loadRecaptcha() is explicitly called from a component

// --- Background warm-up: non-blocking dynamic imports after page load ---
// These imports are intentionally delayed and do not change behavior; they
// simply warm browser/network caches so on-demand imports resolve faster.
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    // Wait a short time after load so we don't compete with critical work
    setTimeout(() => {
      // Import toast & its theme CSS in background (no install) to warm cache
      import('vue-toast-notification')
        .catch(() => {});
      import('vue-toast-notification/dist/theme-sugar.css')
        .catch(() => {});

      // Also warm the reCAPTCHA library so calling loadRecaptcha is quicker
      import('vue-recaptcha-v3')
        .catch(() => {});
    }, 4000);
  });
}